import React, { useState } from 'react';
import { Pressable, View } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { faChevronDown, faChevronUp, faSliders } from '@fortawesome/pro-regular-svg-icons';
import useCustomTheme from '../../../hooks/theme-hook';
import useTranslation from '../../../hooks/translation-hook';
import useMissionTypeFilters from '../../../hooks/useMissionTypeFilters';
import { FilterType } from '../../../models/MissionFilterType';
import { useAppSelector } from '../../../hooks/store-hook';
import { StylesHelper } from '../../../models/helpers/stylesHelper';
import Label, { LabelType } from '../Label';
import CustomSwitch from '../CustomSwitch';
import MissionFiltersPopup, { MissionFiltersPopupProps } from './MissionFiltersPopup';

interface CompactMissionFiltersProps extends MissionFiltersPopupProps {
  expanded?: boolean;
  onExpandedChange?(expanded: boolean): void;
}

export default function CompactMissionFilters(props: CompactMissionFiltersProps) {
  const theme = useCustomTheme();
  const t = useTranslation();
  const missionTypeFilters = useMissionTypeFilters();
  const [localExpanded, setLocalExpanded] = useState(true);
  const expanded = props.expanded ?? localExpanded;
  const language = useAppSelector(state => state.userPrefs.uiLanguage);
  const moreFiltersLabel = t('More-filters', {
    defaultValue: language?.startsWith('fr') ? 'Plus de filtres' : 'More filters',
  });
  const color = theme.text.darkTurquoiseTwo.toString();
  const borderColor = theme.borderColorLight.toString();
  return (
    <View
      testID="compact-mission-filters"
      style={{
        borderRadius: 8,
        backgroundColor: theme.background.primary,
        ...StylesHelper.shadow({ width: 1, height: 1 }, 0.14, 2, theme.overlayColor.toString(), 1),
      }}
    >
      <Pressable
        testID="toggle-mission-filters-button"
        accessibilityRole="button"
        accessibilityLabel={t('Missions-filters')}
        accessibilityState={{ expanded }}
        aria-expanded={expanded}
        onPress={() => {
          setLocalExpanded(!expanded);
          props.onExpandedChange?.(!expanded);
        }}
        style={({ pressed }) => ({
          flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          minHeight: 42, paddingHorizontal: 12, paddingVertical: 8, opacity: pressed ? 0.9 : 1,
        })}
      >
        <FontAwesomeIcon icon={faSliders} size={14} color={color} />
        <Label
          variant={LabelType.titleDarkSmall}
          numberOfLines={1}
          style={{ color, fontWeight: '700', fontSize: 13, textTransform: 'uppercase', flexGrow: 1, flexShrink: 1 }}
        >
          {t('Missions-filters')}
        </Label>
        <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} size={12} color={color} />
      </Pressable>
      {expanded && <View testID="mission-filters-content" style={{ paddingHorizontal: 12, paddingBottom: 12 }}>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
          {missionTypeFilters.map(({ value, label }) => {
            const selected = (props.selectedFilterType ?? FilterType.All) === value;
            return <Pressable key={value} accessibilityRole="button" accessibilityLabel={label} accessibilityState={{ selected }}
              onPress={() => props.onFilterTypeChange?.(value)} style={{ flexGrow: 1, flexShrink: 0, borderRadius: 18, borderWidth: 1,
                borderColor, backgroundColor: selected ? theme.background.lightSea : theme.background.primary,
                minHeight: 36, paddingHorizontal: 8, paddingVertical: 6, alignItems: 'center', justifyContent: 'center' }}>
              <Label numberOfLines={1} style={{ color, fontSize: 12, textAlign: 'center', fontWeight: selected ? '700' : '400' }}>{label}</Label>
            </Pressable>;
          })}
        </View>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', columnGap: 12, rowGap: 8, marginTop: 12, paddingVertical: 2 }}>
          <View style={{ flex: 1, minWidth: 144, minHeight: 32, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <CustomSwitch accessibilityLabel={t('show-unavailable-short')} isEnabled={!props.onlyAcceptableMissions}
              onPress={props.onOnlyAcceptableMissionsChange} />
            <Label style={{ color, fontSize: 12, flexShrink: 1 }}>{t('show-unavailable-short')}</Label>
          </View>
          <View style={{ flex: 1, minWidth: 144, minHeight: 32, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <CustomSwitch accessibilityLabel={t('show-upcoming-short')} isEnabled={props.showUpcomingMissions}
              onPress={props.onShowUpcomingMissionsChange} />
            <Label style={{ color, fontSize: 12, flexShrink: 1 }}>{t('show-upcoming-short')}</Label>
          </View>
        </View>
        <Pressable
          testID="more-mission-filters-button"
          accessibilityRole="button"
          accessibilityLabel={moreFiltersLabel}
          onPress={() => props.onOpenChange?.(true)}
          style={({ pressed }) => ({
            flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8,
            minHeight: 36, marginTop: 12, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 8,
            backgroundColor: theme.background.lightSea, opacity: pressed ? 0.75 : 1,
          })}
        >
          <FontAwesomeIcon icon={faSliders} size={16} color={color} />
          <Label style={{ color, fontWeight: '700', fontSize: 13 }}>{moreFiltersLabel}</Label>
        </Pressable>
      </View>}
      <MissionFiltersPopup {...props} hideTrigger />
    </View>
  );
}
